import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { submitDeveloperContact } from "../../api/profileService";

export default function PricingSection({ content }) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const token = getToken();
  const isLoggedIn = !!token;

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organizationName: "",
    contactNumber: "",
    query: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  // Handle input changes
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Basic email regex for validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Phone number validation
  const phoneRegex = /^\d{10}$/;

  // Validate required fields
  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Email is invalid";

    if (!formData.organizationName.trim()) newErrors.organizationName = "Organisation Name is required";

    if (formData.contactNumber.trim() && !phoneRegex.test(formData.contactNumber.trim()))
      newErrors.contactNumber = "Please enter a valid 10-digit phone number";

    if (!formData.query.trim()) newErrors.query = "Query is required";

    return newErrors;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setApiError("");
    setIsSubmitting(true);

    try {
      // Call the API
      await submitDeveloperContact(
        formData.name,
        formData.email,
        formData.organizationName,
        formData.contactNumber,
        formData.query
      );

      // Show success message
      setSubmitted(true);
      setIsSubmitting(false);
    } catch (error) {
      // Handle API error
      setIsSubmitting(false);
      if (error.response && error.response.data && error.response.data.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError("Failed to submit your query. Please try again later.");
      }
    }
  };

  // Open modal handler for Contact Us button
  const openContactModal = () => {
    setFormData({
      name: "",
      email: "",
      organizationName: "",
      contactNumber: "",
      query: "",
    });
    setErrors({});
    setSubmitted(false);
    setApiError("");
    setModalOpen(true);
  };

  // Close modal handler
  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <section
      id="pricing"
      className="py-8 bg-gradient-to-l from-white via-orange-50 to-orange-200 dark:bg-gradient-to-l dark:from-gray-900 dark:via-gray-900 dark:to-blue-900 pl-0 xl:pl-10"
    >
      <div className="container px-6 pt-8 py-16 mx-auto">
        <div className="xl:items-center xl:-mx-8 xl:flex">
          {/* Heading Section */}
          <div className="flex flex-col items-center xl:w-1/5 xl:items-start xl:mx-8">
            <h1 className="text-3xl font-semibold text-gray-800 capitalize dark:text-white text-center lg:text-4xl">
              {content.pricing.heading}
            </h1>
            <div className="mt-6 text-gray-500 dark:text-gray-300 text-center md:text-start">
              {content.pricing.subheadings.map((subheading) => (
                <p key={subheading} className="mt-2">
                  {subheading}
                </p>
              ))}
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="flex-1 xl:mx-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {content.pricing.plans.map((plan) => {
                const titleClass = `text-lg lg:text-xl font-bold text-transparent bg-clip-text ${plan.titleGradientColors.length === 1
                  ? plan.titleGradientColors[0]
                  : `bg-gradient-to-r ${plan.titleGradientColors.join(" ")}`
                  }`;

                return (
                  <div
                    key={plan.title}
                    className="transition ease-in-out duration-300 transform hover:scale-105 hover:shadow-lg bg-white dark:bg-gray-900 dark:shadow-black/50 mx-auto border rounded-lg p-4 relative"
                  >
                    {plan.title === "Starter Plan" && isLoggedIn && (
                      <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-bold py-1 px-2 rounded-full">
                        Subscribed
                      </div>
                    )}
                    <div className="flex flex-col h-full">
                      <h2 className={titleClass}>{plan.title}</h2>
                      <p className="mt-1 text-orange-700 dark:text-blue-300 opacity-75">
                        {plan.subtext}
                      </p>

                      <div className="mt-4 text-gray-500 dark:text-gray-300">
                        {/* <p className="mb-2">{plan.description}</p> */}
                        <ul className="list-disc pl-4 space-y-1">
                          {plan.features.map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Pricing Display */}
                      {/* {plan.price && (
                        <h3 className="mt-4 text-xl font-bold text-gray-700 sm:text-2xl dark:text-gray-300">
                          ${plan.price.toFixed(2)}{" "}
                          <span className="text-sm font-medium">per token</span>
                        </h3>
                      )} */}
                      {/* {plan.price_monthly && plan.price_annual && (
                        <h3 className="mt-4 text-xl font-bold text-gray-700 sm:text-2xl dark:text-gray-300">
                          ${plan.price_annual}{" "}
                          <span className="text-sm font-medium">per year</span>
                          <div className="text-sm font-medium opacity-50">
                            or ${plan.price_monthly} per month
                          </div>
                        </h3>
                      )} */}

                      {/* CTA */}

                      <div className="mt-auto">
                        {plan.title === "Starter Plan" ? (
                          isLoggedIn ? (
                            <></>
                          ) : (
                            <button
                              onClick={() => navigate("/login")}
                              className="mt-3 w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200"
                            >
                              Start Free Trial
                            </button>
                          )
                        ) : (
                          isLoggedIn ? (
                            <button
                              onClick={openContactModal}
                              className="mt-3 w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200"
                            >
                              Contact Us
                            </button>
                          ) : (
                            <button
                              onClick={() => navigate("/login")}
                              className="mt-3 w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200"
                            >
                              Contact Us
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 w-full max-w-md p-4 relative shadow-lg shadow-gray-300/50 dark:shadow-black/50">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
              aria-label="Close modal"
            >
              &#x2715;
            </button>

            {!submitted ? (
              <>
                <h1 className="text-xl font-bold mb-4 text-center text-gray-800 dark:text-white">
                  Contact Us
                </h1>

                {apiError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                    {apiError}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate className="space-y-4 text-sm">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block mb-1 text-gray-700 dark:text-gray-300">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Your Name"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`block w-full px-3 py-2 rounded-md bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 border ${errors.name
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-200 dark:border-gray-700 focus:border-blue-400"
                        } focus:outline-none focus:ring focus:ring-opacity-40`}
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block mb-1 text-gray-700 dark:text-gray-300">
                      Email ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="example@email.com"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`block w-full px-3 py-2 rounded-md bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 border ${errors.email
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-200 dark:border-gray-700 focus:border-blue-400"
                        } focus:outline-none focus:ring focus:ring-opacity-40`}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Organisation */}
                  <div>
                    <label htmlFor="organizationName" className="block mb-1 text-gray-700 dark:text-gray-300">
                      Organisation Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Your Organisation"
                      id="organizationName"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleChange}
                      className={`block w-full px-3 py-2 rounded-md bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 border ${errors.organizationName
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-200 dark:border-gray-700 focus:border-blue-400"
                        } focus:outline-none focus:ring focus:ring-opacity-40`}
                    />
                    {errors.organizationName && <p className="text-red-500 text-xs mt-1">{errors.organizationName}</p>}
                  </div>

                  {/* Contact Number */}
                  <div>
                    <label htmlFor="contactNumber" className="block mb-1 text-gray-700 dark:text-gray-300">
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      placeholder="10-digit number"
                      id="contactNumber"
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleChange}
                      className={`block w-full px-3 py-2 rounded-md bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 border ${errors.contactNumber
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-200 dark:border-gray-700 focus:border-blue-400"
                        } focus:outline-none focus:ring focus:ring-opacity-40`}
                    />
                    {errors.contactNumber && <p className="text-red-500 text-xs mt-1">{errors.contactNumber}</p>}
                  </div>

                  {/* Query */}
                  <div>
                    <label htmlFor="query" className="block mb-1 text-gray-700 dark:text-gray-300">
                      Your Query <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      placeholder="Type your query here..."
                      id="query"
                      name="query"
                      rows="3"
                      value={formData.query}
                      onChange={handleChange}
                      className={`block w-full px-3 py-2 rounded-md bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 border ${errors.query
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-200 dark:border-gray-700 focus:border-blue-400"
                        } focus:outline-none focus:ring focus:ring-opacity-40`}
                    ></textarea>
                    {errors.query && <p className="text-red-500 text-xs mt-1">{errors.query}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-2 px-4 text-center bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </form>
              </>
            ) : (
              <div className="py-8 text-center">
                <div className="bg-green-100 dark:bg-green-900/20 p-3 rounded-full mb-4 inline-block">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Thank You!
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Your query has been submitted successfully. Our team will contact you shortly.
                </p>
                <button
                  onClick={closeModal}
                  className="py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}


    </section>
  );
}
